import { Form, Formik } from 'formik';

import { useAppDispatch } from '@/shared/hooks/store';
import { PackagingEnum } from '@/shared/types/enums/packaging';
import { CustomersController } from '@/shared/endpoints/customers';
import { IMedication } from '@/shared/types/interfaces/medication';
import { Button, Alert, Input, SimpleRadio } from '@/shared/ui';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';

import { initialValues, OtcMedicationSchema, otcMedicationValidationSchema } from './config';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useCallback } from 'react';

interface IProps {
    medication: IMedication;
    onCreateMedication?: (prescription: ITransferPrescriptions | null) => void;
    transferPrescription?: ITransferPrescriptions;
}

const OtcMedicationForm = ({ medication, transferPrescription, onCreateMedication }: IProps) => {
    const theme = useTheme();
    const isDownSM = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useAppDispatch();

    const onAddMedication = useCallback(
        async (values: OtcMedicationSchema) => {
            if (transferPrescription) await dispatch(CustomersController.removeTransferPrescription(transferPrescription));

            const data = {
                ...values,
                otc: true,
                prescribable: medication,
                toPharmacyNpi: import.meta.env.REACT_APP_PHARMACY_NPI || ''
            } as ITransferPrescriptions;
            try {
                const result = await dispatch(CustomersController.addTransferPrescription(data));
                onCreateMedication?.(result.meta.requestStatus === 'fulfilled' ? data : null);
            } catch (error) {
                toast.error('Failed to add medication');
            }
        },
        [dispatch, medication, onCreateMedication, transferPrescription]
    );

    return (
        <div className="otc-medication-form" data-testid="otc-medication-form">
            <span className="font-agrandir font-medium text-22 md:text-28 lg:text-[1.75rem] mb-[15px] md:mb-[20px] block">{medication.name}</span>
            <Formik<OtcMedicationSchema>
                onSubmit={onAddMedication}
                initialValues={transferPrescription || initialValues}
                validationSchema={otcMedicationValidationSchema}
            >
                {({ values, errors, touched, setFieldValue, handleChange, handleSubmit }) => {
                    const firstErrorKey = Object.keys(errors)[0] as keyof OtcMedicationSchema;
                    const isFirstErrortouched = touched[firstErrorKey];
                    const formHasError = isFirstErrortouched && errors[firstErrorKey];

                    return (
                        <Form data-testid="otc-medication-form-content" onSubmit={handleSubmit}>
                            <p data-testid="otc-medication-form-content-title" className="text-lg font-bold">
                                <span className="text-red-500">*</span> Select Packaging
                            </p>
                            <div className="flex flex-col gap-5 py-5">
                                <SimpleRadio
                                    testId="otc-medication-form-include-in-roll"
                                    className="prescriptions__otc_form_radio"
                                    label="Include in MedBox roll"
                                    error={!!errors.packaging}
                                    checked={values.packaging === PackagingEnum.IncludeInMedbox}
                                    onChange={() => setFieldValue('packaging', PackagingEnum.IncludeInMedbox)}
                                />
                                <SimpleRadio
                                    testId="otc-medication-form-package-separately"
                                    className="prescriptions__otc_form_radio"
                                    label="Package separately"
                                    error={!!errors.packaging}
                                    checked={values.packaging === PackagingEnum.Separately}
                                    onChange={() => setFieldValue('packaging', PackagingEnum.Separately)}
                                />
                            </div>
                            <div className="flex flex-col gap-5">
                                <Input
                                    className="prescriptions__otc_form_direction"
                                    required
                                    boldLabel
                                    name="direction"
                                    onChange={handleChange}
                                    value={values.direction}
                                    placeholder="ex. 2 times a day"
                                    maxLength={50}
                                    label={`How often do you take ${medication.name}, and in what quantity?`}
                                    error={touched.direction && !!errors.direction}
                                    testId="prescription-direction"
                                />
                                <Input
                                    className="prescriptions__otc_form_quantity"
                                    required
                                    boldLabel
                                    type="number"
                                    name="quantity"
                                    placeholder="ex. 30"
                                    onKeyDown={e => {
                                        if (['+', '-'].includes(e.key.toString())) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }
                                    }}
                                    label="Quantity per month"
                                    value={values.quantity}
                                    testId="prescription-quantity"
                                    onChange={e => {
                                        // quick fixes, refactor after
                                        if (/^0/.test(e.currentTarget.value)) {
                                            e.currentTarget.value = e.currentTarget.value.replace(/^0/, '');
                                        }

                                        if (+e.currentTarget.value <= 0 && e.currentTarget.value !== '') return;

                                        handleChange(e);
                                    }}
                                    error={touched.quantity && !!errors.quantity}
                                />
                            </div>
                            {formHasError && <Alert testId="otc-medication-form-error-message" label="Please complete all fields to proceed." className="mt-[25px]" />}
                            <Button
                                testId="save-btn"
                                variant="contained"
                                type="submit"
                                className="prescriptions__otc_form_save_btn mt-[25px] w-full"
                                showEndIcon
                            >
                                Save & Add
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withScrollRestoration(OtcMedicationForm);
